import React, { useState, useEffect } from "react";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Card,
  Row,
  Col,
  Table,
  InputNumber,
  Typography,
  message,
  Divider,
  Alert,
  Avatar,
  Upload,
  List as AntList,
  Popconfirm,
  Switch,
} from "antd";
import { 
  PlusOutlined, 
  DeleteOutlined, 
  UserOutlined, 
  HomeOutlined, 
  BankOutlined, 
  UploadOutlined, 
  InboxOutlined, 
  FilePdfOutlined 
} from "@ant-design/icons";
import dayjs from "dayjs";
import { IInvoice, IInvoiceItem, ISender, IGuest, IInvoiceAttachment, IRecurringSettings } from "../../interfaces";
import { SearchSelectButton } from "../../components/SearchSelectButton";
import { AdditionalProductsSelect } from "../../components/AdditionalProductsSelect";
import { RecurringInvoiceSettings } from "../../components/RecurringInvoiceSettings";
import { DebugApiTest } from "../../components/DebugApiTest";
import { splitAddress, combineAddress, testAddressSplitting } from "../../utils/addressUtils";


const { TextArea } = Input;
const { Title, Text } = Typography;

export const InvoiceEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<IInvoice>({
    onMutationSuccess: () => {
      message.success("Rechnung erfolgreich aktualisiert!");
    },
    onMutationError: (error) => {
      console.error("Error updating invoice:", error);
      message.error("Fehler beim Aktualisieren der Rechnung");
    }
  });
  const invoice = queryResult?.data?.data;

  // State für Formular-Daten
  const [items, setItems] = useState<IInvoiceItem[]>([]);
  const [selectedSender, setSelectedSender] = useState<ISender | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<IGuest | null>(null);
  const [invoiceType, setInvoiceType] = useState<"invoice" | "storno" | "noshow">("invoice");
  const [recurringSettings, setRecurringSettings] = useState<IRecurringSettings | undefined>();
  const [showRecurringSettings, setShowRecurringSettings] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [attachments, setAttachments] = useState<IInvoiceAttachment[]>([]);

  // Test der Adress-Aufspaltung (kann entfernt werden nach dem Test)
  useEffect(() => {
    console.log("🧪 Testing Address Splitting:");
    testAddressSplitting();
  }, []);

  // Lade Daten wenn Invoice verfügbar ist
  useEffect(() => {
    if (invoice) {
      console.log("InvoiceEdit - Loading invoice data:", invoice);
      console.log("InvoiceEdit - Raw JSON available:", invoice.raw_json);
      
      // Setze Items - Sicherstellen dass es ein Array ist
      const invoiceItems = Array.isArray(invoice.items) ? invoice.items : [];
      console.log("InvoiceEdit - Raw invoice data:", invoice);
      console.log("InvoiceEdit - Setting items:", invoiceItems);
      
      // Sicherstellen dass jedes Item eine ID hat
      const itemsWithIds = invoiceItems.map((item: any, index: number) => ({
        ...item,
        id: item.id || Date.now() + index,
        description: item.description || '',
        quantity: item.quantity || 1,
        price: item.price || 0,
        tax: item.tax !== undefined ? item.tax : (item.vat || 19),
        total: item.total || (item.quantity || 1) * (item.price || 0)
      }));
      
      console.log("InvoiceEdit - Processed items:", itemsWithIds);
      setItems(itemsWithIds);
      
      // Setze Sender und Guest - dies aktiviert die Detail-Karten
      if (invoice.sender) {
        setSelectedSender(invoice.sender);
      }
      if (invoice.guest) {
        setSelectedGuest(invoice.guest);
      }
      
      // Setze Typ
      const type = invoice.type === "recurring" ? "invoice" : invoice.type;
      setInvoiceType(type || "invoice");
      setShowRecurringSettings(invoice.type === "recurring");
      
      // Setze Attachments
      setAttachments(invoice.attachments || []);
      
      // Formular mit Invoice-Daten befüllen
      formProps.form?.setFieldsValue({
        number: invoice.number,
        date: invoice.date ? dayjs(invoice.date) : undefined,
        type: invoice.type,
        text: invoice.text,
        notes: invoice.notes,
        sender: {
          id: invoice.sender?.id,
        },
        guest: {
          id: invoice.guest?.id,
          name: invoice.guest?.name,
          firstname: invoice.guest?.firstname,
          lastname: invoice.guest?.lastname,
          email: invoice.guest?.email,
          phone: invoice.guest?.phone,
          // Intelligente Adress-Aufspaltung
          ...(invoice.guest?.street && invoice.guest?.house_number 
            ? { 
                street: invoice.guest.street, 
                house_number: invoice.guest.house_number 
              }
            : (() => {
                const addressSplit = splitAddress(invoice.guest?.address || '');
                return {
                  street: addressSplit.street,
                  house_number: addressSplit.houseNumber
                };
              })()
          ),
          zip: invoice.guest?.zip,
          city: invoice.guest?.city,
          country: invoice.guest?.country,
          company: invoice.guest?.company,
          // Intelligente Firmenadress-Aufspaltung
          ...(invoice.guest?.company_street && invoice.guest?.company_house_number 
            ? { 
                company_street: invoice.guest.company_street, 
                company_house_number: invoice.guest.company_house_number 
              }
            : (() => {
                const companyAddressSplit = splitAddress(invoice.guest?.company_address || '');
                return {
                  company_street: companyAddressSplit.street,
                  company_house_number: companyAddressSplit.houseNumber
                };
              })()
          ),
          company_zip: invoice.guest?.company_zip,
          company_city: invoice.guest?.company_city,
          company_country: invoice.guest?.company_country,
          company_email: invoice.guest?.company_email,
          company_phone: invoice.guest?.company_phone,
          company_tax_id: invoice.guest?.company_tax_id,
        },
        accommodation: {
          name: invoice.accommodation?.name,
          checkin: invoice.accommodation?.checkin ? dayjs(invoice.accommodation.checkin) : undefined,
          checkout: invoice.accommodation?.checkout ? dayjs(invoice.accommodation.checkout) : undefined,
          nights: invoice.accommodation?.nights,
          guests: invoice.accommodation?.guests,
          advert_id: invoice.accommodation?.advert_id,
        },
      });
    }
  }, [invoice, formProps.form]);

  // Helper-Funktion für API-URL
  const getApiUrl = (): string => {
    if (typeof window !== 'undefined') {
      const windowEnv = (window as any).__ENV__;
      if (windowEnv?.REACT_APP_API_URL) {
        return windowEnv.REACT_APP_API_URL;
      }
      return window.location.origin;
    }
    return 'http://localhost';
  };

  // Auto-Fill Funktionen
  const handleSenderSelect = (value: any, option: any) => {
    console.log('handleSenderSelect - value:', value, 'option:', option);
    
    if (!option?.item) {
      console.error('Kein item in option gefunden!');
      return;
    }
    
    setSelectedSender(option.item);
    formProps.form?.setFieldsValue({
      sender: { id: value },
    });

    // In Edit-Modus die bestehenden Unterkunftsdaten beibehalten
    // Nur aktualisieren, wenn explizit neue Daten vorhanden sind
    const currentAccommodation = formProps.form?.getFieldValue('accommodation');
    if (!currentAccommodation?.name && option.item?.accommodation) {
      formProps.form?.setFieldsValue({
        accommodation: {
          name: option.item.accommodation.name || option.item.company || option.item.name,
          address: option.item.accommodation.address || option.item.address,
          zip: option.item.accommodation.zip || option.item.zip,
          city: option.item.accommodation.city || option.item.city,
          country: option.item.accommodation.country || 'Deutschland',
        },
      });
    }

    message.success(`Absender "${option.item.name}" ausgewählt`);
  };

  const handleGuestSelect = (value: any, option: any) => {
    console.log('handleGuestSelect - value:', value, 'option:', option);
    
    if (!option?.item) {
      console.error('Kein item in option gefunden!');
      return;
    }
    
    setSelectedGuest(option.item);
    
    // Erweiterte Gastdaten ausfüllen mit intelligenter Adresssplitting
    const personalAddressSplit = option.item.street && option.item.house_number 
      ? { street: option.item.street, houseNumber: option.item.house_number }
      : splitAddress(option.item.address || '');
    
    const companyAddressSplit = option.item.company_street && option.item.company_house_number
      ? { street: option.item.company_street, houseNumber: option.item.company_house_number }
      : splitAddress(option.item.company_address || '');
    
    formProps.form?.setFieldsValue({
      guest: {
        id: value,
        // Persönliche Daten
        name: option.item.name || '',
        firstname: option.item.firstname || '',
        lastname: option.item.lastname || '',
        email: option.item.email || '',
        phone: option.item.phone || '',
        street: personalAddressSplit.street,
        house_number: personalAddressSplit.houseNumber,
        zip: option.item.zip || '',
        city: option.item.city || '',
        country: option.item.country || 'Deutschland',
        // Firmendaten
        company: option.item.company || '',
        company_email: option.item.company_email || '',
        company_street: companyAddressSplit.street,
        company_house_number: companyAddressSplit.houseNumber,
        company_zip: option.item.company_zip || '',
        company_city: option.item.company_city || '',
        company_country: option.item.company_country || '',
        company_phone: option.item.company_phone || '',
        company_tax_id: option.item.company_tax_id || '',
      },
    });
    message.success(`Gast "${option.item.name}" ausgewählt und alle Daten ausgefüllt`);
  };

  const handleInvoiceTypeChange = (value: "invoice" | "storno" | "noshow") => {
    setInvoiceType(value);
    setShowRecurringSettings(false); // recurring ist im Edit-Modus nicht verfügbar

    if (value === "storno") {
      message.info("Storno-Modus: Beträge werden automatisch negiert");
    } else if (value === "noshow") {
      message.info("No-Show-Modus: Bitte passen Sie die Positionen entsprechend an");
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        id: Date.now(),
        description: "",
        quantity: 1,
        price: 0,
        tax: 19,
        total: 0,
      },
    ]);
  };

  const addItemFromProduct = (newItem: IInvoiceItem) => {
    setItems([...items, newItem]);
    message.success("Zusatzprodukt als Position hinzugefügt");
  };



  const removeItem = (id: number) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    } else {
      message.warning("Mindestens eine Position muss vorhanden sein");
    }
  };

  const updateItem = (id: number, field: keyof IInvoiceItem, value: any) => {
    setItems(
      items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Berechne Total bei Änderung
          if (field === "quantity" || field === "price") {
            updatedItem.total = updatedItem.quantity * updatedItem.price;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const calculateTotals = () => {
    // Sicherstellen, dass items ein Array ist
    const validItems = Array.isArray(items) ? items : [];
    
    const subtotal = validItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax7 = validItems
      .filter((item) => item.tax === 7)
      .reduce((sum, item) => sum + (item.total || 0) * 0.07, 0);
    const tax19 = validItems
      .filter((item) => item.tax === 19)
      .reduce((sum, item) => sum + (item.total || 0) * 0.19, 0);
    const total = subtotal + tax7 + tax19;

    return { subtotal, tax7, tax19, total };
  };

  const totals = calculateTotals();

  // Upload-Konfiguration für Anhänge
  const uploadProps = {
    name: 'file',
    action: `${getApiUrl()}/api/invoices/upload`,
    multiple: true,
    accept: '.jpg,.jpeg,.png,.pdf',
    beforeUpload: (file: any) => {
      const isValidType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
      if (!isValidType) {
        message.error('Nur JPG, PNG und PDF Dateien sind erlaubt!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Datei muss kleiner als 10MB sein!');
        return false;
      }
      return true;
    },
    onChange: (info: any) => {
      const { status } = info.file;
      if (status === 'done') {
        message.success(`${info.file.name} erfolgreich hochgeladen.`);
        // Hier würdest du die Anhänge-Liste aktualisieren
      } else if (status === 'error') {
        message.error(`${info.file.name} Upload fehlgeschlagen.`);
      }
    },
  };

  const columns = [
    {
      title: "Beschreibung",
      dataIndex: "description",
      key: "description",
      render: (_: any, record: IInvoiceItem) => (
        <Input
          value={record?.description || ''}
          onChange={(e) => updateItem(record.id, "description", e.target.value)}
          placeholder="Beschreibung eingeben"
        />
      ),
    },
    {
      title: "Menge",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (_: any, record: IInvoiceItem) => (
        <InputNumber
          min={1}
          value={record?.quantity || 1}
          onChange={(value) => updateItem(record.id, "quantity", value || 1)}
        />
      ),
    },
    {
      title: "Einzelpreis",
      dataIndex: "price",
      key: "price",
      width: 150,
      render: (_: any, record: IInvoiceItem) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record?.price || 0}
          onChange={(value) => updateItem(record.id, "price", value || 0)}
          formatter={(value) => `€ ${value}`}
        />
      ),
    },
    {
      title: "MwSt %",
      dataIndex: "tax",
      key: "tax",
      width: 100,
      render: (_: any, record: IInvoiceItem) => (
        <Select
          value={record.tax || 19}
          onChange={(value) => updateItem(record.id, "tax", value)}
          style={{ width: "100%" }}
        >
          <Select.Option value={0}>0%</Select.Option>
          <Select.Option value={7}>7%</Select.Option>
          <Select.Option value={19}>19%</Select.Option>
        </Select>
      ),
    },
    {
      title: "Gesamt",
      dataIndex: "total",
      key: "total",
      width: 150,
      render: (_: any, record: IInvoiceItem) => (
        <Typography.Text strong>
          € {(record?.total || 0).toFixed(2)}
        </Typography.Text>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_: any, record: IInvoiceItem) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.id)}
        />
      ),
    },
  ];

  // Transform form values before sending to API
  const transformFormValues = (values: any) => {
    // Anpassung für Storno-Rechnungen (Beträge negieren)
    let adjustedItems = [...items];
    if (invoiceType === "storno") {
      adjustedItems = items.map(item => ({
        ...item,
        quantity: -Math.abs(item.quantity),
        total: -Math.abs(item.total),
      }));
    }

    // Kombiniere Straße und Hausnummer für Backwards Compatibility
    const guestAddress = combineAddress(values.guest?.street || '', values.guest?.house_number || '');
    const companyAddress = combineAddress(values.guest?.company_street || '', values.guest?.company_house_number || '');

    // Füge die berechneten Werte hinzu
    const invoiceData: Partial<IInvoice> = {
      ...values,
      type: invoiceType,
      items: adjustedItems,
      totals: invoiceType === "storno" ? {
        subtotal: -Math.abs(totals.subtotal),
        tax7: -Math.abs(totals.tax7),
        tax19: -Math.abs(totals.tax19),
        total: -Math.abs(totals.total),
      } : totals,
      date: values.date?.format("YYYY-MM-DD"),
      // Erweiterte Gastdaten mit Backwards Compatibility
      guest: {
        ...values.guest,
        address: guestAddress,
        company_address: companyAddress,
      },
      accommodation: {
        ...values.accommodation,
        checkin: values.accommodation?.checkin?.format("YYYY-MM-DD"),
        checkout: values.accommodation?.checkout?.format("YYYY-MM-DD"),
      },
      attachments,
      // Bei recurring Rechnungen die Einstellungen beibehalten
      ...(invoice?.type === "recurring" && invoice?.recurring_settings && {
        is_recurring: true,
        recurring_settings: invoice.recurring_settings,
      }),
      // Wichtig: Raw JSON Strukturen für korrekte Speicherung
      raw_json: invoice?.raw_json || {
        calculation: invoice?.calculation || {},
        positions: invoice?.positions || {},
        additional_positions: invoice?.additionalPositions || [],
        taxes: invoice?.taxes || [],
        booking_personal: invoice?.bookingPersonal || {},
        booking_timing: invoice?.bookingTiming || {}
      }
    };

    console.log("Transformed Invoice Data:", invoiceData);
    console.log("Raw JSON included:", invoiceData.raw_json);
    return invoiceData;
  };

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form 
        {...formProps} 
        layout="vertical" 
        onFinish={(values) => {
          const transformedData = transformFormValues(values);
          // Refine's useForm hooks into onFinish to handle the API call
          return formProps.onFinish?.(transformedData);
        }}>
        <Row gutter={[16, 16]}>
          {/* Absender und Rechnungsdaten */}
          <Col span={24}>
            <Card title="Rechnungsdaten">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Absender"
                    name={["sender", "id"]}
                    rules={[{ required: true, message: "Bitte Absender auswählen" }]}
                  >
                    <SearchSelectButton<ISender>
                      resource="senders"
                      title="Absender auswählen"
                      placeholder="Klicken um Absender zu suchen"
                      searchFields={["name", "company", "email"]}
                      renderItem={(item) => ({
                        title: item.company || item.name,
                        description: `${item.address || ''} ${item.city || ''}`,
                        meta: item.email,
                        avatar: <Avatar icon={<BankOutlined />} style={{ backgroundColor: '#1890ff' }} />
                      })}
                      value={formProps.form?.getFieldValue(["sender", "id"])}
                      displayValue={selectedSender?.company || selectedSender?.name || invoice?.sender?.company || invoice?.sender?.name}
                      onChange={(value, item) => {
                        if (item) {
                          handleSenderSelect(value, { item });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Rechnungsnummer"
                    name="number"
                    rules={[{ required: true, message: "Bitte Rechnungsnummer eingeben" }]}
                  >
                    <Input placeholder="z.B. MM-1001" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Rechnungsdatum"
                    name="date"
                    rules={[{ required: true, message: "Bitte Datum auswählen" }]}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Rechnungstyp"
                    name="type"
                    initialValue="invoice"
                  >
                    <Select
                      placeholder="Rechnungstyp auswählen"
                      value={invoiceType}
                      onChange={handleInvoiceTypeChange}
                    >
                      <Select.Option value="invoice">Standard-Rechnung</Select.Option>
                      <Select.Option value="storno">Storno-Rechnung</Select.Option>
                      <Select.Option value="noshow">No-Show-Rechnung</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Absender-Details und Gastdaten nebeneinander */}
          {(selectedSender || selectedGuest || invoice?.sender || invoice?.guest) && (
            <Col span={24}>
              <Row gutter={16}>
                {/* Absender-Details */}
                {(selectedSender || invoice?.sender) && (
          <Col span={12}>
                    <Card 
                      title="Absender-Details" 
                      size="small"
                      style={{ backgroundColor: '#f5f5f5' }}
                    >
                      <Row gutter={[8, 8]}>
                        <Col span={24}>
                          <Text strong>{selectedSender?.name || invoice?.sender?.name}</Text>
                          {(selectedSender?.company || invoice?.sender?.company) && (
                            <div>
                              <Text type="secondary">{selectedSender?.company || invoice?.sender?.company}</Text>
                            </div>
                          )}
                        </Col>
                        <Col span={24}>
                          <Text type="secondary">{selectedSender?.address || invoice?.sender?.address}</Text>
                        </Col>
                        <Col span={24}>
                          <Text type="secondary">{selectedSender?.zip || invoice?.sender?.zip} {selectedSender?.city || invoice?.sender?.city}</Text>
                        </Col>
                        {(selectedSender?.email || invoice?.sender?.email) && (
                          <Col span={24}>
                            <Text type="secondary">E-Mail: {selectedSender?.email || invoice?.sender?.email}</Text>
                          </Col>
                        )}
                        {(selectedSender?.phone || invoice?.sender?.phone) && (
                          <Col span={24}>
                            <Text type="secondary">Tel: {selectedSender?.phone || invoice?.sender?.phone}</Text>
                          </Col>
                        )}
                        {(selectedSender?.tax_id || invoice?.sender?.tax_id) && (
                          <Col span={24}>
                            <Text type="secondary">USt-IdNr: {selectedSender?.tax_id || invoice?.sender?.tax_id}</Text>
                          </Col>
                        )}
                        {((selectedSender?.iban || invoice?.sender?.iban) || (selectedSender?.bank_name || invoice?.sender?.bank_name)) && (
                          <>
                            <Divider style={{ margin: '8px 0' }} />
                            {(selectedSender?.bank_name || invoice?.sender?.bank_name) && (
                              <Col span={24}>
                                <Text type="secondary">Bank: {selectedSender?.bank_name || invoice?.sender?.bank_name}</Text>
                              </Col>
                            )}
                            {(selectedSender?.iban || invoice?.sender?.iban) && (
                              <Col span={24}>
                                <Text type="secondary">IBAN: {selectedSender?.iban || invoice?.sender?.iban}</Text>
                              </Col>
                            )}
                            {(selectedSender?.bic || invoice?.sender?.bic) && (
                              <Col span={24}>
                                <Text type="secondary">BIC: {selectedSender?.bic || invoice?.sender?.bic}</Text>
                              </Col>
                            )}
                          </>
                        )}
                      </Row>
                    </Card>
                  </Col>
                )}
                
                {/* Gast-Details (wenn ausgewählt) */}
                {(selectedGuest || invoice?.guest) && (
                  <Col span={12}>
                    <Card 
                      title="Gast-Details (ausgewählt)" 
                      size="small"
                      style={{ backgroundColor: '#e6f7ff' }}
                    >
                      <Row gutter={[8, 8]}>
                        <Col span={24}>
                          <Text strong>{selectedGuest?.name || invoice?.guest?.name}</Text>
                        </Col>
                        {(selectedGuest?.email || invoice?.guest?.email) && (
                          <Col span={24}>
                            <Text type="secondary">E-Mail: {selectedGuest?.email || invoice?.guest?.email}</Text>
                          </Col>
                        )}
                        {(selectedGuest?.phone || invoice?.guest?.phone) && (
                          <Col span={24}>
                            <Text type="secondary">Tel: {selectedGuest?.phone || invoice?.guest?.phone}</Text>
                          </Col>
                        )}
                        {(selectedGuest?.address || selectedGuest?.street || invoice?.guest?.address || invoice?.guest?.street) && (
                          <Col span={24}>
                            <Text type="secondary">
                              {selectedGuest?.address || 
                               (selectedGuest?.street && selectedGuest?.house_number ? `${selectedGuest.street} ${selectedGuest.house_number}` : selectedGuest?.street) ||
                               invoice?.guest?.address ||
                               (invoice?.guest?.street && invoice?.guest?.house_number ? `${invoice.guest.street} ${invoice.guest.house_number}` : invoice?.guest?.street)}
                            </Text>
                          </Col>
                        )}
                        {(selectedGuest?.zip || selectedGuest?.city || invoice?.guest?.zip || invoice?.guest?.city) && (
                          <Col span={24}>
                            <Text type="secondary">
                              {selectedGuest?.zip || invoice?.guest?.zip} {selectedGuest?.city || invoice?.guest?.city}
                            </Text>
                          </Col>
                        )}
                        {(selectedGuest?.country || invoice?.guest?.country) && (
                          <Col span={24}>
                            <Text type="secondary">{selectedGuest?.country || invoice?.guest?.country}</Text>
                          </Col>
                        )}
                        {/* Firmendaten anzeigen falls vorhanden */}
                        {(selectedGuest?.company || invoice?.guest?.company) && (
                          <>
                            <Divider style={{ margin: '8px 0' }}>Firmendaten</Divider>
                            <Col span={24}>
                              <Text type="secondary" strong>{selectedGuest?.company || invoice?.guest?.company}</Text>
                            </Col>
                            {(selectedGuest?.company_address || invoice?.guest?.company_address || 
                              selectedGuest?.company_street || invoice?.guest?.company_street) && (
                              <Col span={24}>
                                <Text type="secondary">
                                  {selectedGuest?.company_address || invoice?.guest?.company_address ||
                                   (selectedGuest?.company_street && selectedGuest?.company_house_number ? 
                                    `${selectedGuest.company_street} ${selectedGuest.company_house_number}` : selectedGuest?.company_street) ||
                                   (invoice?.guest?.company_street && invoice?.guest?.company_house_number ? 
                                    `${invoice.guest.company_street} ${invoice.guest.company_house_number}` : invoice?.guest?.company_street)}
                                </Text>
                              </Col>
                            )}
                            {(selectedGuest?.company_zip || selectedGuest?.company_city || 
                              invoice?.guest?.company_zip || invoice?.guest?.company_city) && (
                              <Col span={24}>
                                <Text type="secondary">
                                  {selectedGuest?.company_zip || invoice?.guest?.company_zip} {selectedGuest?.company_city || invoice?.guest?.company_city}
                                </Text>
                              </Col>
                            )}
                            {(selectedGuest?.company_tax_id || invoice?.guest?.company_tax_id) && (
                              <Col span={24}>
                                <Text type="secondary">USt-IdNr: {selectedGuest?.company_tax_id || invoice?.guest?.company_tax_id}</Text>
                              </Col>
                            )}
                          </>
                        )}
                      </Row>
            </Card>
          </Col>
                )}
              </Row>
            </Col>
          )}

          {/* Gast - Persönliche Daten */}
          <Col span={24}>
            <Card title="Gast - Persönliche Daten" size="small">
              <Row gutter={16}>
                <Col span={12}>
              <Form.Item
                label="Gast auswählen"
                name={["guest", "id"]}
                rules={[{ required: true, message: "Bitte Gast auswählen" }]}
              >
                <SearchSelectButton<IGuest>
                  resource="guests"
                  title="Gast auswählen"
                  placeholder="Klicken um Gast zu suchen"
                  searchFields={["name", "firstname", "lastname", "email", "company"]}
                  renderItem={(item) => ({
                    title: item.name,
                    description: item.company ? `${item.company} - ${item.address || ''}` : item.address,
                    meta: item.email,
                    avatar: <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                  })}
                  value={formProps.form?.getFieldValue(["guest", "id"])}
                      displayValue={selectedGuest?.name || invoice?.guest?.name}
                      onChange={(value, item) => {
                        if (item) {
                          handleGuestSelect(value, { item });
                        }
                      }}
                />
              </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item label="Anrede/Name" name={["guest", "name"]}>
                    <Input placeholder="Anrede/Name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Vorname" name={["guest", "firstname"]}>
                    <Input placeholder="Vorname" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Nachname" name={["guest", "lastname"]}>
                    <Input placeholder="Nachname" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="E-Mail" name={["guest", "email"]}>
                    <Input type="email" placeholder="E-Mail Adresse" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Telefon" name={["guest", "phone"]}>
                    <Input placeholder="Telefonnummer" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Straße" name={["guest", "street"]}>
                    <Input placeholder="Straßenname" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Hausnummer" name={["guest", "house_number"]}>
                    <Input placeholder="Nr." />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="PLZ" name={["guest", "zip"]}>
                    <Input placeholder="PLZ" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Ort" name={["guest", "city"]}>
                    <Input placeholder="Stadt" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Land" name={["guest", "country"]}>
                    <Input placeholder="Land" defaultValue="Deutschland" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Gast - Firmendaten */}
          <Col span={24}>
            <Card title="Gast - Firmendaten (optional)" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Firmenname" name={["guest", "company"]}>
                    <Input placeholder="Firmenname" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Firmen-E-Mail" name={["guest", "company_email"]}>
                    <Input type="email" placeholder="Firmen E-Mail" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Firmen-Telefon" name={["guest", "company_phone"]}>
                    <Input placeholder="Firmen Telefon" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Firmen-Straße" name={["guest", "company_street"]}>
                    <Input placeholder="Firmen Straße" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Hausnummer" name={["guest", "company_house_number"]}>
                    <Input placeholder="Nr." />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="PLZ" name={["guest", "company_zip"]}>
                    <Input placeholder="PLZ" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Ort" name={["guest", "company_city"]}>
                    <Input placeholder="Firmen Stadt" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Firmen-Land" name={["guest", "company_country"]}>
                    <Input placeholder="Firmen Land" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="USt-IdNr" name={["guest", "company_tax_id"]}>
                    <Input placeholder="USt-IdNr (falls vorhanden)" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Unterkunft & Aufenthalt */}
          <Col span={24}>
            <Card title="Unterkunft & Aufenthalt" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Unterkunft Name" name={["accommodation", "name"]}>
                    <Input placeholder="Name der Unterkunft" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label="Check-in" 
                    name={["accommodation", "checkin"]}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label="Check-out" 
                    name={["accommodation", "checkout"]}
                    getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD.MM.YYYY"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Anzahl Nächte" name={["accommodation", "nights"]}>
                    <InputNumber min={1} placeholder="Nächte" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Anzahl Gäste" name={["accommodation", "guests"]}>
                    <InputNumber min={1} placeholder="Gäste" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Advert ID" name={["accommodation", "advert_id"]}>
                    <InputNumber placeholder="Advert ID (optional)" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Rechnungspositionen */}
          <Col span={24}>
            <Card
              title="Rechnungspositionen"
              size="small"
              extra={
                <Space>
                  <AdditionalProductsSelect 
                    senderId={formProps.form?.getFieldValue(["sender", "id"])}
                    onAddToInvoice={addItemFromProduct}
                  />
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={addItem}
                >
                  Position hinzufügen
                </Button>
                </Space>
              }
            >
              <Table
                dataSource={Array.isArray(items) ? items : []}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{ emptyText: "Keine Positionen vorhanden. Klicken Sie auf 'Position hinzufügen' um zu beginnen." }}
                footer={() => (
                  <div style={{ textAlign: 'right' }}>
                    <Space direction="vertical" size={4}>
                      <div>Zwischensumme: <Text strong>€ {totals.subtotal.toFixed(2)}</Text></div>
                      {totals.tax7 > 0 && (
                        <div>MwSt 7%: <Text strong>€ {totals.tax7.toFixed(2)}</Text></div>
                      )}
                      {totals.tax19 > 0 && (
                        <div>MwSt 19%: <Text strong>€ {totals.tax19.toFixed(2)}</Text></div>
                      )}
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ fontSize: '16px' }}>
                        Gesamtbetrag: <Text strong style={{ fontSize: '18px' }}>€ {totals.total.toFixed(2)}</Text>
                      </div>
                    </Space>
                  </div>
                )}
              />
            </Card>
          </Col>

          {/* Weitere Belege / Anhänge */}
          <Col span={24}>
            <Card title="Weitere Belege" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Upload.Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Klicken oder Dateien hierher ziehen zum Hochladen
                    </p>
                    <p className="ant-upload-hint">
                      Unterstützte Formate: JPG, PNG, PDF (max. 10MB)
                    </p>
                  </Upload.Dragger>
                </Col>
                <Col span={12}>
                  {attachments.length > 0 && (
                    <AntList
                      size="small"
                      header={<div>Hochgeladene Dateien</div>}
                      dataSource={attachments}
                      renderItem={(item) => (
                        <AntList.Item
                          actions={[
                            <Button key="download" type="link" icon={<FilePdfOutlined />}>
                              Download
                            </Button>,
                            <Popconfirm
                              key="delete"
                              title="Datei löschen?"
                              onConfirm={() => {
                                setAttachments(attachments.filter(a => a.id !== item.id));
                                message.success('Datei gelöscht');
                              }}
                            >
                              <Button type="link" danger icon={<DeleteOutlined />}>
                                Löschen
                              </Button>
                            </Popconfirm>
                          ]}
                        >
                          <AntList.Item.Meta
                            avatar={<FilePdfOutlined />}
                            title={item.original_name}
                            description={`${(item.size / 1024).toFixed(1)} KB - ${item.type.toUpperCase()}`}
                          />
                        </AntList.Item>
                      )}
                    />
                  )}
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Rechnungstext & Notizen */}
          <Col span={24}>
            <Card title="Rechnungstext & Notizen" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Rechnungstext" name="text">
                    <TextArea 
                      rows={4} 
                      placeholder="Text für die Rechnung (wird auf der Rechnung angezeigt)" 
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Interne Notizen" name="notes">
                    <TextArea 
                      rows={4} 
                      placeholder="Interne Notizen (nicht auf der Rechnung sichtbar)" 
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
}; 